/// <amd-module name="Build/DesignerExtensions/ExtendsCheckPanel/Components/Ref" />
import * as Resources from "Build/DesignerExtensions/ExtendsCheckPanel/Resources";
import { format } from "VSS/Core/Util/String";

export const EMPTY_OBJECT_ID = "0000000000000000000000000000000000000000";

const invalidRefNameStrings = ["~", "^", ":", " ", "?", "*", "[", "\\", "/.", ".lock/", "//", "..", "@{"];
const invalidRefLeadNameStrings = ["/", "."];
const invalidRefTrailNameStrings = [".lock", ".", "/"];
const maxGitRefNameLength = 400;
const refsPrefix = "refs/";
const refsHeadsPrefix = `${refsPrefix}heads/`;
const refsTagsPrefix = `${refsPrefix}tags/`;

/**
 * Deterime whether or not the given ref name is valid and if not, return an appropriate error message
 *
 * @param branchName Name of the branch to check
 */
export function getInvalidBranchNameMessage(branchName: string): string | undefined {
    if (!branchName) {
        return Resources.BranchNameErrorEmpty;
    } else if (branchName.length + refsHeadsPrefix.length > maxGitRefNameLength) {
        return Resources.BranchNameErrorTooLong;
    }

    for (const str of invalidRefNameStrings) {
        if (branchName.indexOf(str) >= 0) {
            return format(Resources.BranchNameErrorInvalidCharacters, str);
        }
    }

    for (const str of invalidRefLeadNameStrings) {
        if (branchName.startsWith(str)) {
            return format(Resources.BranchNameErrorInvalidStartCharacters, str);
        }
    }

    for (const str of invalidRefTrailNameStrings) {
        if (branchName.endsWith(str)) {
            return format(Resources.BranchNameErrorInvalidEndCharacters, str);
        }
    }

    return undefined;
}

/**
 * Deterime whether or not the given tag name is valid and if not, return an appropriate error message
 *
 * @param tagName Name of the tag to check
 */
export function getInvalidTagNameMessage(tagName: string): string | undefined {
    if (!tagName) {
        return Resources.TagNameErrorEmpty;
    } else if (tagName.length + refsTagsPrefix.length > maxGitRefNameLength) {
        return Resources.TagNameErrorTooLong;
    }

    for (const str of invalidRefNameStrings) {
        if (tagName.indexOf(str) >= 0) {
            return format(Resources.TagNameErrorInvalidCharacter, str);
        }
    }

    for (const str of invalidRefLeadNameStrings) {
        if (tagName.startsWith(str)) {
            return format(Resources.TagNameErrorInvalidLeadCharacter, str);
        }
    }

    for (const str of invalidRefTrailNameStrings) {
        if (tagName.endsWith(str)) {
            return format(Resources.TagNameErrorInvalidTrailCharacter, str);
        }
    }

    return undefined;
}

export function getShortCommitId(full: string): string {
    return full.substring(0, 8);
}

export function isEmptyObjectId(objectId: string): boolean {
    return !objectId || objectId === EMPTY_OBJECT_ID;
}

/**
 * Strips off the "refs/heads/" portion of a ref name to
 * get the branch name
 *
 * @param refName Branch name in the form refs/heads/<branch name>
 */
export function refToBranchName(refName: string): string {
    return (refName || "").startsWith(refsHeadsPrefix) ? refName.substr(refsHeadsPrefix.length) : refName;
}

/**
 * Strips off the "refs/tags/" or "refs/heads/" portion of a ref name to
 * get the tag or branch name
 *
 * @param refName Tag or branch name in the form refs/<tags | heads>/<tag name>
 */
export function refToBranchOrTagName(refName: string): string {
    if (isBranch(refName)) {
        return refToBranchName(refName);
    }

    return refToTagName(refName);
}

/**
 * Strips off the "refs/tags/" portion of a ref name to
 * get the tag name
 *
 * @param refName Tag name in the form refs/tags/<tag name>
 */
export function refToTagName(refName: string): string {
    return (refName || "").startsWith(refsTagsPrefix) ? refName.substr(refsTagsPrefix.length) : refName;
}

/**
 * Prepends "refs/heads/" to a branch to create a ref name
 *
 * @param branchName Branch name in the form <branch name>
 */
export function branchNameToRef(branchName: string): string {
    return branchName.startsWith(refsHeadsPrefix) ? `${branchName}` : `${refsHeadsPrefix}${branchName}`;
}

/**
 * Prepends "refs/tags/" to a tag to create a ref name
 *
 * @param tagName Tag name in the form <tag name>
 */
export function tagNameToRef(tagName: string): string {
    return tagName.startsWith(refsTagsPrefix) ? `${tagName}` : `${refsTagsPrefix}${tagName}`;
}

export function getFriendlyName(refName: string): string {
    if (isBranch(refName)) {
        return refName.substr(refsHeadsPrefix.length);
    } else if (isTag(refName)) {
        return refName.substr(refsTagsPrefix.length);
    } else {
        return refName;
    }
}

export function isBranch(refName: string): boolean {
    return (refName || "").indexOf(refsHeadsPrefix) === 0;
}

export function isTag(refName: string): boolean {
    return (refName || "").indexOf(refsTagsPrefix) === 0;
}

export function getRefNameFromBranchName(branchName: string): string {
    return refsHeadsPrefix + branchName;
}

export function getRefNameFromTagName(branchName: string): string {
    return refsTagsPrefix + branchName;
}

export function trimRefs(refName: string): string {
    return (refName || "").startsWith(refsPrefix) ? refName.substr(refsPrefix.length) : refName;
}
